import React, { useId, useRef, useState } from 'react';
import TemplatePicker from '../notes/TemplatePicker';
import NotePreview from './NotePreview';
import SendButton from './SendButton';
import ImagePicker from '../../resources/ImagePicker';
import { useFilesIfProvided } from '../../../data/useFiles';
// The core's, not markdown's: the renderer stays out of this chunk (NotePreview.jsx).
import { insertNoteImage, resourceMessage } from '../../../domain/resources';
import { applyTemplate } from '../../../domain/noteTemplates';
import { NOTE_BODY_MAX, noteCounterText } from '../../../domain/lessonNotes';
import { useLocale } from '../../../context/LocaleContext';
import { NOTE_AUDIENCES, noteAudience, notePlaceholder } from '../../../domain/lessonAttendance';
import s from './attendance.module.css';
import t from '../notes/noteTemplates.module.css';

// Lesson notes, one body per audience.
//
// Three separate notes rather than one note with a visibility setting, as in
// MMS: switching audience changes both the placeholder and the "visible to"
// line, so the audience selects which note is being edited.
//
// ── Markdown source in the box, rendered when it is read ────────────────────
//
// A knowing divergence from MMS, which runs a full WYSIWYG here — Froala,
// storing HTML, with a toolbar and a raw-source view — and mails what it
// stores.
//
//   * Nothing typed here is stored as markup: the textarea's value is exactly
//     what reaches Firestore (#74). Storing a teacher's HTML would make a
//     sanitiser the only thing between an adversary's markup and a family's
//     inbox; src/domain/markdown.js argues this.
//   * The text is Markdown, rendered at read time — in the email to the family
//     and in the preview panel below — with `html: false`, so raw HTML in a
//     note is escaped. The supported subset is
//     docs/features/lesson-note-markdown.md.
//   * No toolbar: Preview lets the teacher write plainly and check what a
//     family will get.

// The three audiences — who each note is visible to — are `NOTE_AUDIENCES` in
// src/domain/lessonAttendance.js.

export default function LessonNotesTab({
  audience, onAudienceChange, notes, onNoteChange,
  recipients = [], notesTo = [], onToggleNotesTo,
  // The Email Lesson Notes button (#732): `sends.notes` from
  // useAttendanceForm, and what pressing it does. Absent, no button.
  send = null, onSend,
  // The Linked Online Resources section (#424), rendered by the drawer and
  // handed in, so this tab — mounted on its own by tests with no library
  // provider — does not need one.
  linkedResources = null,
}) {
  const current = noteAudience(audience);
  const [preview, setPreview] = useState(false);
  const previewId = useId();
  const { locale } = useLocale();
  const counter = noteCounterText(notes[current.id], NOTE_BODY_MAX, locale);

  // Insert Image (#531): a picture from the studio's library, written into the
  // note at the cursor as a reference by id (`insertNoteImage`, the core's, as
  // both phones write it — #562). Only where there is a library to pick from:
  // tests mount this tab with no provider, and then no picker is offered.
  const library = useFilesIfProvided();
  const box = useRef(null);
  const [picking, setPicking] = useState(false);
  const [noRoom, setNoRoom] = useState(null);
  const insertImage = (row) => {
    const el = box.current;
    const text = notes[current.id] || '';
    const at = el ? [el.selectionStart, el.selectionEnd] : [text.length, text.length];
    const { value, cursor, refusal } = insertNoteImage(text, at[0], at[1], row);
    // Refused whole rather than clamped: half a reference is a broken one.
    if (refusal) {
      setNoRoom(refusal.message);
      return;
    }
    setNoRoom(null);
    onNoteChange(current.id, value);
    // After React has put the new value in the box: the cursor goes after the
    // picture, so the next word typed lands below it.
    requestAnimationFrame(() => {
      if (!box.current) return;
      box.current.focus();
      box.current.setSelectionRange(cursor, cursor);
    });
  };

  return (
    <>
      <div className={s.notesHead}>
        <span className={s.notesLabel}>Show notes for:</span>
        <span className={s.visibility}>{current.visibility}</span>
      </div>

      <div className={s.audienceRow}>
        {NOTE_AUDIENCES.map((a) => (
          <button
            key={a.id}
            className={audience === a.id ? s.audienceOn : s.audience}
            aria-pressed={audience === a.id}
            onClick={() => onAudienceChange(a.id)}
          >
            {/* A filled body is marked, so a teacher can see at a glance that
                there is a parent note without clicking through to find it. */}
            {notes[a.id]?.trim() ? <span aria-hidden="true">• </span> : null}
            {a.label}
          </button>
        ))}
      </div>

      {/* `maxLength` is firestore.rules' bound, reached here rather than at
          save time; otherwise the refusal would arrive as a write that does
          nothing — to the charge and the credit too, since the attendance mark
          is one batch. */}
      <textarea
        ref={box}
        rows={9}
        maxLength={NOTE_BODY_MAX}
        className={s.input}
        value={notes[current.id] || ''}
        placeholder={notePlaceholder(current.id)}
        onChange={(e) => onNoteChange(current.id, e.target.value)}
      />

      {/* ── The counter, a knowing divergence from MMS (#93) ──

          MMS's Froala editor has a running character counter whose toolbar is
          set to `display: none`: a lesson note there has no length limit, and
          MMS shows counters only where a limit exists (255 on a template
          description, 140 on an SMS segment). Ours has a limit, so it gets a
          counter. At 65,535 characters nobody types up to it, so the count is a
          plain running figure and the limit appears only in the last thousand
          characters. An empty box says nothing.

          The full state names the template case, the one way to reach the bound
          without meaning to: applyTemplate appends and setNote clamps, so a
          template applied to a full note adds nothing.

          No `aria-live`: this changes on every keystroke, and announcing each
          one would make the box unusable with a screen reader. The full state
          is a sentence, so it reads as an explanation when reached. */}
      {counter ? <div className={s.hint}>{counter}</div> : null}

      {/* `Templates` sits at the foot of the editor, as in MMS. It appends to
          whichever note is on screen and is not per-audience: the same practice
          instruction is as likely to go in the parent note as the student one. */}
      <div className={t.pickerRow}>
        <div className={s.editorTools}>
          <TemplatePicker
            onApply={(body) => onNoteChange(current.id, applyTemplate(notes[current.id] || '', body))}
          />
          {/* Beside `Templates`, because it belongs to the editor as that
              does. A toggle rather than a second textarea: the drawer is
              narrow, and side-by-side would give each half about twenty
              characters of line.

              `aria-expanded` and not `aria-pressed`, though it is styled like
              the audience pills: this reveals a panel rather than turning a
              setting on, and a button carrying both is announced twice. */}
          <button
            type="button"
            className={s.previewToggle}
            aria-expanded={preview}
            aria-controls={previewId}
            onClick={() => setPreview((p) => !p)}
          >
            Preview
          </button>
          {library ? (
            <button type="button" className={s.previewToggle} onClick={() => setPicking(true)}>
              {resourceMessage('insert_image')}
            </button>
          ) : null}
        </div>
        {/* The line-breaks clause matters: `breaks: true` is what makes a note
            typed one item per line render one item per line. Not linked to
            docs/features/lesson-note-markdown.md, a repo document with no URL
            a teacher could open; the Preview button beside it is the
            teacher-facing answer. */}
        <span className={s.hint}>
          Markdown — **bold**, *italic*, - lists, tables. Line breaks are kept.
        </span>
      </div>

      {noRoom ? (
        <div className={s.hint} role="alert">{noRoom}</div>
      ) : null}
      {library ? <ImagePicker open={picking} onPick={insertImage} onClose={() => setPicking(false)} /> : null}

      {/* Only the body being edited: the three are separate notes, and
          rendering all of them would put the private note beside the parent
          one as a teacher decides what to send. The private note is never sent
          (functions/delivery/templates.js never receives it), so this panel is
          the only place it is drawn. */}
      {preview ? (
        <NotePreview id={previewId} source={notes[current.id] || ''} label={current.label} />
      ) : null}

      {/* Between the editor and Email Lesson Notes, where MMS puts it. */}
      {linkedResources}

      {/* MMS's "Email Lesson Notes": a checkbox per family contact plus
          "Myself", none ticked by default, so a note written for a private
          audience cannot go out because a previous lesson's note did.

          Sent from its own button, not from Save (#732) — a knowing difference
          from MMS, which emails the ticked people when Save is pressed. The
          button saves the drawer first, so what is emailed is what is on
          record (useAttendanceForm.js). */}
      <div className={`${s.notesHead} ${s.emailHead}`}>
        <span className={s.notesLabel}>Email Lesson Notes</span>
      </div>

      {/* `recipients` is `notesRecipients` (domain/notifications.js): the
          contacts, then Myself unless a contact already has that address. */}
      {recipients.length ? (
        <div className={s.recipients}>
          {recipients.map((r) => (
            <label key={r.address} className={s.checkRow}>
              <input
                type="checkbox"
                checked={notesTo.includes(r.address)}
                onChange={() => onToggleNotesTo?.(r.address)}
              />
              <span>{r.name || r.address}</span>
            </label>
          ))}
        </div>
      ) : (
        <div className={s.muted}>
          No email address on this family, so there is nobody to send them to.
        </div>
      )}

      {recipients.length ? <SendButton send={send} onSend={onSend} /> : null}
    </>
  );
}
